// Billing & expenses data layer — the pass-through cost ledger, ported from
// legacy loadBilling/renderBilling/execSaveExpense/deleteExpense/exportBillingReport.
//
// ── The ONE write this phase adds (Rule 2) ────────────────────────────────
// Reports is read-only. Billing is the single exception: it inserts expense rows
// and soft-deletes them. Both writes are BYTE-IDENTICAL to v_20's — same table,
// same column set, same `deleted_at` soft-delete (never a hard DELETE: an
// expense is a financial record and KRA audit evidence, so it is voided, not
// erased), same audit line. buildExpenseRow() is pure and unit-tested precisely
// so the insert shape cannot drift from the legacy one unnoticed.
//
// ── Why the totals live here and not in the view ──────────────────────────
// The category rollup feeds three surfaces: the on-screen breakdown, the XLSX
// invoice and the sponsor statement. One function, three readers — the same
// R-022 discipline the metrics registry applies to Reports. A sponsor invoice
// that disagrees with the screen it was exported from is a billing dispute.
//
// NOTE ON THE COST METRICS: Reports computes cost_per_patient / cost_breakdown
// from its OWN snapshot of the expenses table (data/reports.js, same
// `.is('deleted_at', null)` guard). This module does not feed them and must not
// — a second writer of the reporting snapshot is the R-013 bug in miniature.

import { getClient, fetchAll, ok, fail } from '../db/client.js';
import { TABLES, BUCKETS } from '../db/contract.js';
import { logAudit, AUDIT_EVENT } from './audit.js';
import { getXLSX } from './import.js';
import { csvSafe } from '../ui/esc.js';

// ─── Vocabulary (legacy EXPENSE_LABELS, in the legacy order) ──────────────

export const EXPENSE_CATEGORIES = Object.freeze([
  Object.freeze({ value: 'registration', label: 'Registration Fee' }),
  Object.freeze({ value: 'sha_premium', label: 'SHA Premium' }),
  Object.freeze({ value: 'courier', label: 'Courier / Delivery' }),
  Object.freeze({ value: 'lab', label: 'Lab Costs' }),
  Object.freeze({ value: 'dispatch_evidence', label: 'Dispatch Evidence' }),
  Object.freeze({ value: 'personnel', label: 'Personnel' }),
  Object.freeze({ value: 'per_diem', label: 'Per Diem' }),
  Object.freeze({ value: 'operational', label: 'Operational' }),
  Object.freeze({ value: 'other', label: 'Other' }),
]);

// dispatch_evidence is a PHOTO RECORD, not a payment: it carries amount 0 and is
// never billable. It is excluded from the add-expense picker (it has its own
// modal) but kept in the label map so existing rows render with a real name.
export const PAYABLE_CATEGORIES = Object.freeze(EXPENSE_CATEGORIES.filter((c) => c.value !== 'dispatch_evidence'));

export const PAYMENT_METHODS = Object.freeze([
  Object.freeze({ value: 'mpesa', label: 'M-Pesa' }),
  Object.freeze({ value: 'cash', label: 'Cash' }),
  Object.freeze({ value: 'bank_transfer', label: 'Bank Transfer' }),
  Object.freeze({ value: 'cheque', label: 'Cheque' }),
  Object.freeze({ value: 'card', label: 'Card' }),
]);

/** Human label for a category value (unknown values render as themselves). @param {string} v @returns {string} */
export function categoryLabel(v) {
  const c = EXPENSE_CATEGORIES.find((x) => x.value === v);
  return c ? c.label : String(v || '—').replace(/_/g, ' ');
}

/** Human label for a payment method. @param {string} v @returns {string} */
export function methodLabel(v) {
  const m = PAYMENT_METHODS.find((x) => x.value === v);
  return m ? m.label : String(v || '—').replace(/_/g, ' ');
}

// ─── Date presets (month-boundary-safe) ───────────────────────────────────

/** 'YYYY-MM-DD' for a local Date (never an ISO/UTC round trip). @param {Date} d @returns {string} */
export function isoDay(d) {
  return (
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  );
}

/**
 * The This-month / Last-month presets as an inclusive {from, to} day range.
 *
 * THE BUG THIS AVOIDS (ported from legacy setBillingMonth): the day-of-month is
 * pinned to the 1st BEFORE the month is shifted. `new Date(2026,0,31)` then
 * setMonth(-1) rolls 31 December → 31 December is fine, but 31 March stepped
 * back a month lands on 2 March (February has no 31st) and "last month" silently
 * becomes a 3-day window. Pin first, shift second.
 *
 * @param {'this'|'last'} which
 * @param {Date} [now]
 * @returns {{from: string, to: string}}
 */
export function monthPreset(which, now) {
  const base = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const anchor = new Date(base.getFullYear(), base.getMonth(), 1); // pin to the 1st FIRST
  if (which === 'last') anchor.setMonth(anchor.getMonth() - 1);
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const last = new Date(y, m + 1, 0); // day 0 of next month = last day of this one
  return { from: isoDay(new Date(y, m, 1)), to: isoDay(last) };
}

// ─── Pure filtering, totals and grouping ──────────────────────────────────

/**
 * Narrow a loaded expense list to one category. 'all' (or blank) is a pass-through.
 * @param {any[]} rows @param {string} [category] @returns {any[]}
 */
export function filterByCategory(rows, category) {
  const list = Array.isArray(rows) ? rows : [];
  if (!category || category === 'all') return list.slice();
  return list.filter((e) => e && e.category === category);
}

/** Parse an amount cell to a finite number; anything unparseable is 0, never NaN. @param {any} v @returns {number} */
export function amountOf(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v === null || v === undefined ? '' : v));
  return isFinite(n) ? n : 0;
}

/**
 * Headline totals. Every field is total and finite for an empty list — a billing
 * screen that renders NaN on a quiet month is a screen nobody trusts with money.
 * @param {any[]} rows
 * @returns {{total: number, billable: number, nonBillable: number, count: number, patients: number}}
 */
export function expenseTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let total = 0;
  let billable = 0;
  const patients = new Set();
  for (const e of list) {
    const a = amountOf(e && e.amount);
    total += a;
    if (e && e.is_billable) billable += a;
    if (e && e.case_id) patients.add(e.case_id);
  }
  return {
    total: Math.round(total * 100) / 100,
    billable: Math.round(billable * 100) / 100,
    nonBillable: Math.round((total - billable) * 100) / 100,
    count: list.length,
    patients: patients.size,
  };
}

/**
 * Category rollup, largest spend first. `share` is the percentage of the grand
 * total and is GUARDED: an all-zero month yields share 0, not NaN or Infinity
 * (a 0-width bar is correct; a NaN-width bar collapses the layout).
 * @param {any[]} rows
 * @returns {Array<{key: string, label: string, count: number, total: number, share: number}>}
 */
export function groupByCategory(rows) {
  const list = Array.isArray(rows) ? rows : [];
  /** @type {Record<string, {count: number, total: number}>} */
  const by = {};
  for (const e of list) {
    const k = String((e && e.category) || 'other');
    if (!by[k]) by[k] = { count: 0, total: 0 };
    by[k].count++;
    by[k].total += amountOf(e && e.amount);
  }
  const grand = Object.keys(by).reduce((a, k) => a + by[k].total, 0);
  return Object.keys(by)
    .map((k) => ({
      key: k,
      label: categoryLabel(k),
      count: by[k].count,
      total: Math.round(by[k].total * 100) / 100,
      share: grand > 0 ? Math.round((by[k].total / grand) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
}

/**
 * Receipt/evidence storage paths for an expense row. `evidence_urls` is a JSON
 * STRING in the legacy schema (not a JSONB array), so a bad parse must degrade
 * to "no receipts" rather than throw inside a list render.
 * @param {any} e
 * @returns {string[]}
 */
export function parseEvidence(e) {
  if (!e) return [];
  let urls = [];
  try {
    urls = typeof e.evidence_urls === 'string' ? JSON.parse(e.evidence_urls || '[]') : e.evidence_urls || [];
  } catch {
    urls = [];
  }
  if (!Array.isArray(urls)) urls = [];
  const out = urls.filter((u) => typeof u === 'string' && u);
  if (e.receipt_url && !out.includes(e.receipt_url)) out.unshift(e.receipt_url);
  return out;
}

/** True when an expense carries at least one receipt/evidence file. @param {any} e @returns {boolean} */
export function hasReceipt(e) {
  return parseEvidence(e).length > 0;
}

// ─── The insert row (PURE — byte-identical to v_20 execSaveExpense) ────────

/**
 * Build the expenses insert row. Every column, default and null-coalescing rule
 * is the legacy one; the ONLY behavioural change is that `is_billable` is now
 * carried from the caller instead of hard-coded true (the column already exists
 * and v_20 wrote it — it simply never let anyone set it, so every programme-level
 * cost was invoiced to the sponsor whether or not it was recoverable).
 *
 * @param {{
 *   caseId?: string|null, programmeId?: string|null, patientName?: string|null,
 *   category: string, description?: string, amount?: number, paymentMethod?: string,
 *   paidTo?: string, paymentReference?: string, etimsInvoiceNo?: string, expenseDate: string,
 *   notes?: string, receiptPaths?: string[], isBillable?: boolean,
 *   productCode?: string|null, batchNumber?: string|null, quantityDispatched?: number|null
 * }} input
 * @param {{userId?: string|null}} [actor]
 * @returns {Record<string, any>}
 */
export function buildExpenseRow(input, actor) {
  const i = input || /** @type {any} */ ({});
  const urls = (i.receiptPaths || []).filter(Boolean);
  /** @param {any} s */
  const t = (s) => {
    const v = String(s === null || s === undefined ? '' : s).trim();
    return v || null;
  };
  /** @type {Record<string, any>} */
  const row = {
    case_id: i.caseId || null,
    programme_id: i.programmeId || null,
    category: i.category,
    description: t(i.description),
    amount: amountOf(i.amount),
    payment_method: i.paymentMethod || 'mpesa',
    paid_to: t(i.paidTo),
    payment_reference: t(i.paymentReference),
    patient_name: t(i.patientName),
    expense_date: i.expenseDate,
    receipt_url: urls[0] || null,
    evidence_urls: JSON.stringify(urls),
    notes: t(i.notes),
    is_billable: i.isBillable !== false,
    created_by: (actor && actor.userId) || null,
  };
  // eTIMS invoice number (KRA compliance): only when the caller supplied it, so
  // older call sites keep writing the exact legacy row shape until the
  // expenses.etims_invoice_no migration has landed everywhere.
  if (i.etimsInvoiceNo !== undefined) row.etims_invoice_no = t(i.etimsInvoiceNo);
  // Dispatch-evidence columns only when the caller supplied them — the legacy
  // expense insert does not carry these keys at all, and adding nulls would be a
  // different row shape from the one production has always written.
  if (i.productCode !== undefined) row.product_code = t(i.productCode);
  if (i.batchNumber !== undefined) row.batch_number = t(i.batchNumber);
  if (i.quantityDispatched !== undefined) row.quantity_dispatched = i.quantityDispatched || 1;
  return row;
}

/**
 * Validate the add-expense form before it can be submitted. Returns an error
 * SENTENCE or null. Ported from the legacy execSaveExpense guards.
 * @param {{category?: string, amount?: any, expenseDate?: string}} input
 * @returns {string|null}
 */
export function validateExpense(input) {
  const i = input || {};
  if (!i.category) return 'Pick a category';
  if (!i.expenseDate) return 'Pick a date';
  if (i.category !== 'dispatch_evidence') {
    const n = parseFloat(String(i.amount === null || i.amount === undefined ? '' : i.amount));
    if (!isFinite(n)) return 'Enter a valid amount';
    if (n < 0) return 'An amount cannot be negative';
  }
  return null;
}

// ─── R-023 · the sponsor period statement (PURE) ──────────────────────────

/**
 * A sponsor statement for one programme and period: category line items with
 * counts and totals, plus a grand total and the stamp that makes it quotable.
 *
 * BILLABLE ONLY by default. A sponsor statement is an invoice-out: it must carry
 * what the sponsor is being asked to fund, not everything the programme spent.
 * The two figures are BOTH reported (`grandTotal` vs `spentTotal`) so the
 * difference is visible rather than quietly dropped — a statement whose total is
 * lower than the ledger's, with no explanation, reads as an error.
 *
 * @param {any[]} rows expenses already narrowed to the period
 * @param {{programmeLabel?: string, from: string, to: string, generatedAt?: string, billableOnly?: boolean}} scope
 * @returns {{
 *   lines: Array<{key: string, label: string, count: number, total: number, share: number}>,
 *   grandTotal: number, spentTotal: number, excludedTotal: number,
 *   count: number, patients: number, programmeLabel: string,
 *   from: string, to: string, generatedAt: string, billableOnly: boolean
 * }}
 */
export function buildStatement(rows, scope) {
  const s = scope || /** @type {any} */ ({});
  const all = Array.isArray(rows) ? rows : [];
  const billableOnly = s.billableOnly !== false;
  const billed = billableOnly ? all.filter((e) => e && e.is_billable) : all.slice();
  const lines = groupByCategory(billed);
  const billedTotals = expenseTotals(billed);
  const allTotals = expenseTotals(all);
  return {
    lines,
    grandTotal: billedTotals.total,
    spentTotal: allTotals.total,
    excludedTotal: Math.round((allTotals.total - billedTotals.total) * 100) / 100,
    count: billedTotals.count,
    patients: billedTotals.patients,
    programmeLabel: s.programmeLabel || 'All programmes',
    from: s.from || '',
    to: s.to || '',
    generatedAt: s.generatedAt || '',
    billableOnly,
  };
}

/**
 * The statement + detail workbook as pure sheet AoAs — the exact content is
 * unit-testable with no SheetJS present.
 *
 * CSV-SAFETY NOTE, ported verbatim from the legacy comment: aoa_to_sheet stores
 * strings INERTLY, so free-text cells do NOT go through csvSafe() — its leading
 * apostrophe would corrupt the stored value. csvSafe is applied only where a
 * cell could be re-exported as CSV from the workbook, i.e. the DB-sourced text
 * columns in the detail sheets, which is where a leading '=' would become a live
 * formula in a spreadsheet the finance team opens.
 *
 * PATIENT DETAIL IS OPT-IN, and this default is the whole point. The workbook is
 * titled "Sponsor statement" and is built to be emailed to an external funder —
 * the same forwarding path the deck refuses to put a name on. It used to append
 * one sheet per category in which every row carried patient_name, paid_to,
 * description and payment_reference: a named, itemised, per-patient spend ledger,
 * with no small-cell logic and no way for the operator to see it before sending
 * (the on-screen preview showed only the category lines). So the sponsor artefact
 * is now aggregate-only, and the named ledger is a SEPARATE, explicitly-labelled
 * internal reconciliation export — `internal: true`, which the view role-gates.
 *
 * @param {ReturnType<typeof buildStatement>} stmt
 * @param {any[]} rows the underlying expense rows (for the detail sheets)
 * @param {{internal?: boolean}} [opts] `internal` adds the per-patient ledgers
 * @returns {Array<{name: string, aoa: any[][], cols: number[]}>}
 */
export function buildStatementSheets(stmt, rows, opts) {
  const internal = !!(opts && opts.internal);
  const list = Array.isArray(rows) ? rows : [];
  /** @type {any[][]} */
  const summary = [
    ['TUMAINI HAVEN PATIENT SUPPORT PROGRAMME'],
    ['Sponsor statement — pass-through costs'],
    ['Programme: ' + stmt.programmeLabel],
    ['Period: ' + stmt.from + ' to ' + stmt.to],
    ['Generated: ' + stmt.generatedAt],
    [stmt.billableOnly ? 'Billable items only' : 'All recorded items (billable and non-billable)'],
    [],
    ['Category', 'Transactions', 'Total (KES)'],
  ];
  for (const l of stmt.lines) summary.push([l.label, l.count, l.total]);
  summary.push([]);
  summary.push(['TOTAL BILLED', stmt.count, stmt.grandTotal]);
  if (stmt.billableOnly && stmt.excludedTotal) {
    // Disclosed, not hidden: the reader can see the ledger is larger than the bill.
    summary.push(['Recorded but not billable', '', stmt.excludedTotal]);
    summary.push(['TOTAL RECORDED SPEND', '', stmt.spentTotal]);
  }

  /** @type {Array<{name: string, aoa: any[][], cols: number[]}>} */
  const sheets = [{ name: 'Statement', aoa: summary, cols: [30, 15, 18] }];
  if (!internal) return sheets; // the sponsor artefact stops here

  summary.push([]);
  summary.push(['INTERNAL RECONCILIATION COPY — contains patient identifiers. Not for sponsors.']);

  const used = new Set(['statement']);
  /** Excel sheet names: ≤31 chars, no : \ / ? * [ ], unique case-insensitively. @param {string} n */
  const safeSheet = (n) => {
    const base = String(n || 'Sheet').replace(/[:\\/?*[\]]/g, '-').slice(0, 31) || 'Sheet';
    let nm = base;
    let i = 2;
    while (used.has(nm.toLowerCase())) {
      const suf = '-' + i;
      nm = base.slice(0, 31 - suf.length) + suf;
      i++;
    }
    used.add(nm.toLowerCase());
    return nm;
  };

  for (const l of stmt.lines) {
    const items = list.filter((e) => e && e.category === l.key && (!stmt.billableOnly || e.is_billable));
    if (!items.length) continue;
    /** @type {any[][]} */
    const aoa = [
      [l.label],
      ['Date', 'Patient', 'Paid To', 'Description', 'Payment Method', 'Reference', 'Amount (KES)', 'Receipt'],
    ];
    for (const e of items) {
      aoa.push([
        e.expense_date || '',
        csvSafe(e.patient_name || '—'),
        csvSafe(e.paid_to || '—'),
        csvSafe(e.description || '—'),
        methodLabel(e.payment_method),
        csvSafe(e.payment_reference || '—'),
        amountOf(e.amount),
        hasReceipt(e) ? 'Yes' : 'No',
      ]);
    }
    aoa.push([]);
    aoa.push(['', '', '', '', '', 'SUBTOTAL', l.total]);
    sheets.push({ name: safeSheet(l.label), aoa, cols: [12, 24, 24, 30, 16, 20, 14, 8] });
  }
  return sheets;
}

/**
 * Write the statement workbook to a download. Returns the envelope so the caller
 * can toast honestly — an offline SheetJS is reported, never silently skipped.
 * @param {ReturnType<typeof buildStatement>} stmt
 * @param {any[]} rows
 * @param {string} [filename]
 * @param {{internal?: boolean}} [opts]
 * @returns {{ok: boolean, data: any, error: any, code: string|null}}
 */
export function writeStatementWorkbook(stmt, rows, filename, opts) {
  const XLSX = getXLSX();
  if (!XLSX) return fail(new Error('xlsx not loaded'), 'NO_XLSX');
  const internal = !!(opts && opts.internal);
  try {
    const sheets = buildStatementSheets(stmt, rows, { internal });
    // The sponsor workbook is asserted, not merely assumed, to be free of
    // identifiers — the same guarantee the deck makes, applied to the sibling
    // export that actually gets emailed to funders. The internal copy is exempt
    // by definition: carrying the names IS its purpose, and it says so on sheet 1.
    if (!internal) {
      const leaks = findStatementIdentifierLeaks(sheets, rows);
      if (leaks.length) return fail(new Error('Statement blocked — it would carry patient identifiers: ' + leaks.join('; ')));
    }
    const wb = XLSX.utils.book_new();
    for (const s of sheets) {
      const ws = XLSX.utils.aoa_to_sheet(s.aoa);
      ws['!cols'] = s.cols.map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, s.name);
    }
    XLSX.writeFile(wb, filename || statementFileName(stmt));
    return ok(null);
  } catch (e) {
    return fail(e);
  }
}

/**
 * Scan a built sheet set for identifier VALUES drawn from the rows it was built
 * over. The billing analogue of deck.js's findIdentifierLeaks — same reasoning
 * (an assertion that runs at export time catches a metric that starts leaking in
 * production, not only in CI), same 4-character needle floor so a short name
 * cannot collide with a legitimate category label and cry wolf.
 *
 * @param {Array<{aoa: any[][]}>} sheets
 * @param {any[]} rows the expense rows the sheets were built from
 * @returns {string[]} violations (empty = safe)
 */
export function findStatementIdentifierLeaks(sheets, rows) {
  /** @type {string[]} */
  const bad = [];
  const list = Array.isArray(rows) ? rows : [];
  /** @type {Array<{v: string, k: string}>} */
  const needles = [];
  for (const e of list) {
    for (const k of ['patient_name', 'reference_number', 'patient_phone', 'national_id', 'payment_reference']) {
      const v = e && e[k];
      if (typeof v === 'string' && v.trim().length >= 4) needles.push({ v: v.trim().toLowerCase(), k });
    }
  }
  if (!needles.length) return bad;

  /** @type {string[]} */
  const cells = [];
  for (const s of Array.isArray(sheets) ? sheets : []) {
    for (const row of (s && s.aoa) || []) {
      for (const c of row || []) if (typeof c === 'string') cells.push(c.toLowerCase());
    }
  }
  const hay = cells.join(' ');
  for (const n of needles) {
    if (hay.includes(n.v)) {
      bad.push(`expense ${n.k} value appears in the statement`);
      break; // one report is enough; the export aborts either way
    }
  }
  return bad;
}

/** The statement download name — programme + period, so two are never confused. @param {any} stmt @returns {string} */
export function statementFileName(stmt) {
  const prog = String((stmt && stmt.programmeLabel) || 'All')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'All';
  return `Tumaini_Statement_${prog}_${(stmt && stmt.from) || ''}_to_${(stmt && stmt.to) || ''}.xlsx`;
}

// ─── Loaders and writers (IMPURE) ─────────────────────────────────────────

const EXPENSE_COLS = '*';

/**
 * Load every expense in an inclusive day range. Chunk-looped through fetchAll —
 * a busy quarter passes 1,000 rows and a capped read would understate the total
 * spend on an invoice (the R-013 failure, in the one place it costs money).
 *
 * Soft-deleted rows are excluded at the source, exactly as the reporting
 * snapshot does, so the two surfaces cannot disagree about what was spent.
 *
 * @param {{from: string, to: string, client?: any}} opts
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null, total?: number}>}
 */
export async function loadExpenses(opts) {
  const o = opts || /** @type {any} */ ({});
  const client = o.client || getClient();
  if (!o.from || !o.to) return fail(new Error('a from and to date are required'), 'BAD_RANGE');
  const res = await fetchAll(() =>
    client
      .from(TABLES.EXPENSES)
      .select(EXPENSE_COLS, { count: 'exact' })
      .gte('expense_date', o.from)
      .lte('expense_date', o.to)
      .is('deleted_at', null)
      .order('expense_date', { ascending: false })
  );
  if (!res.ok) {
    console.warn('loadExpenses', res.error);
    return fail(res.error);
  }
  return Object.assign(ok(res.data || []), { total: res.total });
}

/**
 * Upload receipt files to the private expense-receipts bucket, returning the
 * STORAGE PATHS (never public URLs — the bucket is private and every view signs
 * a short-lived URL on demand).
 *
 * Partial failure is REPORTED, not swallowed: the caller tells the user exactly
 * how many files did not attach, because an expense saved without its receipt is
 * an expense that fails a KRA audit and nobody would know until then.
 *
 * @param {any[]} files a FileList or array
 * @param {{client?: any, prefix?: string}} [deps]
 * @returns {Promise<{paths: string[], failed: number}>}
 */
export async function uploadReceipts(files, deps) {
  const client = (deps && deps.client) || getClient();
  const prefix = (deps && deps.prefix) || '';
  /** @type {string[]} */
  const paths = [];
  let failed = 0;
  for (const file of Array.from(files || [])) {
    const name = String((file && file.name) || 'file');
    const ext = name.includes('.') ? name.split('.').pop() : 'bin';
    const path = prefix + Date.now() + '_' + Math.random().toString(36).slice(2, 8) + '.' + ext;
    try {
      const { error } = await client.storage
        .from(BUCKETS.EXPENSE_RECEIPTS)
        .upload(path, file, { contentType: file.type, upsert: true });
      if (error) throw error;
      paths.push(path);
    } catch (e) {
      console.warn('uploadReceipts', e);
      failed++;
    }
  }
  return { paths, failed };
}

/**
 * Insert one expense row + its audit line. The insert is the legacy one; the
 * audit entry is new (v_20 audited deletions but not creations, which left a
 * ledger where money could appear with no trace of who put it there).
 * @param {Record<string, any>} row from buildExpenseRow
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function insertExpense(row, deps) {
  const client = (deps && deps.client) || getClient();
  try {
    const { error } = await client.from(TABLES.EXPENSES).insert([row]);
    if (error) throw error;
  } catch (e) {
    console.warn('insertExpense', e);
    return fail(e);
  }
  await logAudit(
    AUDIT_EVENT.EXPENSE_CREATED,
    'recorded a ' + categoryLabel(row.category) + ' expense (KES ' + amountOf(row.amount) + ')',
    row.case_id || null,
    { category: row.category, amount: amountOf(row.amount), is_billable: !!row.is_billable },
    { client }
  );
  return ok(row);
}

/**
 * Void an expense — a SOFT delete (`deleted_at`), byte-identical to v_20. The
 * row stays in the table: financial records are voided, never erased.
 * @param {string} expenseId
 * @param {{amount?: any, caseId?: string|null, category?: string}} [meta] for the audit line
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function softDeleteExpense(expenseId, meta, deps) {
  const client = (deps && deps.client) || getClient();
  if (!expenseId) return fail(new Error('no expense id'), 'BAD_ID');
  try {
    const { error } = await client
      .from(TABLES.EXPENSES)
      .update({ deleted_at: new Date().toISOString() })
      .eq('expense_id', expenseId);
    if (error) throw error;
  } catch (e) {
    console.warn('softDeleteExpense', e);
    return fail(e);
  }
  const hasAmt = !!meta && meta.amount !== null && meta.amount !== undefined;
  const amt = hasAmt ? ' (KES ' + amountOf(meta && meta.amount) + ')' : '';
  await logAudit(
    AUDIT_EVENT.EXPENSE_DELETED,
    'removed an expense' + amt,
    (meta && meta.caseId) || null,
    { expense_id: expenseId, amount: hasAmt ? amountOf(meta && meta.amount) : null },
    { client }
  );
  return ok(expenseId);
}

/**
 * A short-lived signed URL for a receipt in the private bucket.
 * @param {string} path @param {{client?: any, expiresIn?: number}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function signedReceiptUrl(path, deps) {
  const client = (deps && deps.client) || getClient();
  try {
    const { data, error } = await client.storage
      .from(BUCKETS.EXPENSE_RECEIPTS)
      .createSignedUrl(path, (deps && deps.expiresIn) || 300);
    if (error) throw error;
    return ok((data && data.signedUrl) || null);
  } catch (e) {
    console.warn('signedReceiptUrl', e);
    return fail(e);
  }
}
